import { timeframeSeconds, type Bar, type Timeframe } from '../chart/chartModel';
import {
  INDICATORS,
  defaultParams,
  indicatorById,
  normalizeParams,
  type IndicatorDef,
  type Params,
} from './library';
import { isIndicatorSource } from './defs';
import type { Series } from './math';

export interface OutputStyle {
  color: string;
  width: 1 | 2 | 3;
  visible: boolean;
}

/** One indicator on one chart: what it is, its parameters and how each of its lines is drawn. Saved with the layout. */
export interface IndicatorInstance {
  uid: string;
  type: string;
  params: Params;
  styles: Record<string, OutputStyle>;
  /** Reference lines of its window, when the user changed them (MT5's Levels tab); left out, the indicator's own apply. */
  levels?: number[];
}

/** The most levels one indicator window may have. */
export const MAX_LEVELS = 10;

export interface Point {
  time: number;
  value: number;
  /** A bar's own colour, for the indicators that colour each bar (the Awesome Oscillator's green and red). */
  color?: string;
}

export interface RenderedOutput {
  key: string;
  label: string;
  style: 'line' | 'histogram' | 'dots';
  color: string;
  width: 1 | 2 | 3;
  points: Point[];
}

export interface RenderedIndicator {
  uid: string;
  type: string;
  title: string;
  overlay: boolean;
  levels: number[];
  outputs: RenderedOutput[];
}

const uid = () => `i${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
const HEX = /^#[0-9a-fA-F]{6}$/;

export function defaultStyles(def: IndicatorDef): Record<string, OutputStyle> {
  return Object.fromEntries(def.outputs.map((o) => [o.key, { color: o.color, width: 2, visible: true }]));
}

export function newInstance(def: IndicatorDef): IndicatorInstance {
  return { uid: uid(), type: def.id, params: defaultParams(def), styles: defaultStyles(def) };
}

/** Reads an instance from stored data, which may be old or damaged: unknown types are dropped, the rest repaired. */
export function normalizeInstance(raw: unknown): IndicatorInstance | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Partial<IndicatorInstance>;
  const def = typeof r.type === 'string' ? indicatorById(r.type) : undefined;
  if (!def) return null;
  const defaults = defaultStyles(def);
  const styles: Record<string, OutputStyle> = {};
  for (const output of def.outputs) {
    const stored = r.styles?.[output.key];
    styles[output.key] = {
      color: stored && HEX.test(stored.color) ? stored.color : defaults[output.key]!.color,
      width: stored?.width === 1 || stored?.width === 3 ? stored.width : 2,
      visible: stored?.visible !== false,
    };
  }
  const levels = Array.isArray(r.levels)
    ? r.levels.filter((l): l is number => typeof l === 'number' && Number.isFinite(l)).slice(0, MAX_LEVELS)
    : undefined;
  return {
    uid: typeof r.uid === 'string' && r.uid ? r.uid : uid(),
    type: def.id,
    params: normalizeParams(def, r.params),
    styles,
    ...(levels ? { levels } : {}),
  };
}

export const normalizeInstances = (raw: unknown): IndicatorInstance[] =>
  Array.isArray(raw) ? raw.map(normalizeInstance).filter((i): i is IndicatorInstance => i !== null) : [];

/**
 * Computes an indicator over the bars and turns it into points for the chart. Outputs with a shift are moved along the time
 * axis by that many bars (Ichimoku's cloud is drawn ahead, its Chikou span behind).
 */
export function renderIndicator(
  instance: IndicatorInstance,
  bars: Bar[],
  tf: Timeframe,
  source?: Series,
): RenderedIndicator | null {
  return renderWithData(instance, bars, tf, source)?.rendered ?? null;
}

/**
 * Renders a chart's indicators in order. One whose "Apply to" is "Previous indicator's data" or "First indicator's data" is
 * computed on the first line of that indicator (as MT5 does); with no such indicator it has nothing to show.
 */
export function renderIndicators(
  instances: IndicatorInstance[],
  bars: Bar[],
  tf: Timeframe,
): RenderedIndicator[] {
  const firstLines: (Series | undefined)[] = [];
  const out: RenderedIndicator[] = [];
  instances.forEach((instance, index) => {
    const applied = instance.params.applied;
    const source = !isIndicatorSource(applied)
      ? undefined
      : applied === 'first'
        ? index === 0
          ? undefined
          : firstLines[0]
        : firstLines[index - 1];
    const result = renderWithData(instance, bars, tf, source);
    firstLines.push(result?.firstLine);
    if (result) out.push(result.rendered);
  });
  return out;
}

function renderWithData(
  instance: IndicatorInstance,
  bars: Bar[],
  tf: Timeframe,
  source?: Series,
): { rendered: RenderedIndicator; firstLine: Series | undefined } | null {
  const def = indicatorById(instance.type);
  if (!def) return null;
  const computed = def.compute(bars, instance.params, source);
  const step = timeframeSeconds(tf);
  const outputs: RenderedOutput[] = [];
  for (const output of def.outputs) {
    const style = instance.styles[output.key];
    if (!style?.visible) continue;
    const series = computed[output.key] ?? [];
    const shift = output.shift?.(instance.params) ?? 0;
    const points: Point[] = [];
    const palette = output.palette ? [style.color, ...output.palette.slice(1)] : null;
    for (let i = 0; i < bars.length; i++) {
      const value = series[i];
      if (value === null || value === undefined || !Number.isFinite(value)) continue;
      const point: Point = { time: bars[i]!.t + shift * step, value };
      if (palette && output.paletteIndex)
        point.color = palette[output.paletteIndex(computed, i, bars)] ?? style.color;
      points.push(point);
    }
    outputs.push({
      key: output.key,
      label: output.label,
      style: output.style,
      color: style.color,
      width: style.width,
      points: shift < 0 ? points.filter((p) => p.time >= (bars[0]?.t ?? 0)) : points,
    });
  }
  const applied = instance.params.applied;
  const suffix = applied === 'prev' ? ' on previous' : applied === 'first' ? ' on first' : '';
  return {
    rendered: {
      uid: instance.uid,
      type: def.id,
      title: def.title(instance.params) + suffix,
      overlay: def.overlay,
      levels: instance.levels ?? def.levels ?? [],
      outputs,
    },
    firstLine: computed[def.outputs[0]?.key ?? ''],
  };
}

export { INDICATORS };
